import type { ExcelColumn } from "@/types/excel";
import { formatINR } from "@/utils/app-formatters";
import { formatListDate } from "@/utils/date-format";
import { exportToExcel } from "@/utils/export-to-excel";
import type { Identifier, RaRecord } from "react-admin";

export type YearlyLedgerExportRecord = RaRecord<Identifier> & {
  receiptNo?: string | null;
  receiptDate?: string | Date | null;
  receiptType?: "CREDIT" | "DEBIT" | string | null;
  paymentMode?: string | null;
  amount?: number | string | null;
  purpose?: string | null;
  name?: string | null;
  itsNo?: string | null;
  paymentRef?: string | null;
  remarks?: string | null;
};

export const YEARLY_LEDGER_EXPORT_COLUMNS: ExcelColumn<YearlyLedgerExportRecord>[] = [
  { header: "Receipt No", field: "receiptNo", width: 15 },
  {
    header: "Date",
    field: "receiptDate",
    width: 15,
    formatter: (_rec, v) => (v ? formatListDate(String(v)) : ""),
  },
  { header: "Type", field: "receiptType", width: 12 },
  { header: "Payment Mode", field: "paymentMode", width: 15 },
  {
    header: "Amount",
    field: "amount",
    width: 15,
    formatter: (_rec, v) => formatINR(v),
  },
  { header: "Purpose", field: "purpose", width: 25 },
  { header: "Name", field: "name", width: 30 },
  { header: "ITS No", field: "itsNo", width: 15 },
  { header: "Payment Ref", field: "paymentRef", width: 20 },
  { header: "Remarks", field: "remarks", width: 28 },
];

export interface ExportYearlyLedgerOptions {
  ledgerTypeName?: string | null;
  ledgerTypeCode?: string | null;
  financialYear?: string | null;
  paymentMode?: string | null;
}

export function yearlyLedgerExportFilenamePrefix(options: ExportYearlyLedgerOptions = {}): string {
  const { ledgerTypeName, ledgerTypeCode, financialYear, paymentMode } = options;
  const typePart = (ledgerTypeCode || ledgerTypeName || "ledger")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
  const fyPart = (financialYear || "all-years")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
  const modePart = (paymentMode || "all")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");

  return `yearly-ledger-${typePart}-${fyPart}-${modePart}`;
}

export function yearlyLedgerExportSheetName(options: ExportYearlyLedgerOptions = {}): string {
  const { ledgerTypeName } = options;
  const name = ledgerTypeName ? `${ledgerTypeName} Ledger` : "Yearly Ledger";
  return name.slice(0, 31);
}

export function exportYearlyLedgerToExcel(
  records: RaRecord[],
  options: ExportYearlyLedgerOptions = {}
): { filename: string; blob: Blob } {
  return exportToExcel(YEARLY_LEDGER_EXPORT_COLUMNS, records as YearlyLedgerExportRecord[], {
    filenamePrefix: yearlyLedgerExportFilenamePrefix(options),
    sheetName: yearlyLedgerExportSheetName(options),
  });
}
